import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Buildings, Plus, MapPin, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DemoBanner, HelpTip } from "@/components/DemoHelpers";
import { useNotifications } from "@/lib/notifications";
import { ownerDisplayName } from "@/lib/ownerDisplay";
import PropertyDeadlineAlert from "@/components/PropertyDeadlineAlert";
import NewPropertyForm, { emptyNewPropertyForm } from "@/components/NewPropertyForm";
import AiPropertyCreate, { attachAnalyzedDocs } from "@/pages/marketplace/stages/AiPropertyCreate";

const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1638885930125-85350348d266?crop=entropy&cs=srgb&fm=jpg&w=600",
  "https://images.unsplash.com/photo-1602872029708-84d970d3382b?crop=entropy&cs=srgb&fm=jpg&w=600",
  "https://images.unsplash.com/photo-1720247522884-75029b97e0b5?crop=entropy&cs=srgb&fm=jpg&w=600",
];

const emptyForm = () => emptyNewPropertyForm();

export default function Properties() {
  const [active, setActive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  // Iter209eu — Scelta iniziale nel dialog: null | "manual" | "ai".
  const [createMode, setCreateMode] = useState(null);
  const [search, setSearch] = useState("");
  // Iter93: pallini rossi per notifiche non lette per immobile
  const { propertySummary } = useNotifications();
  const perProperty = propertySummary?.per_property || {};

  const load = async () => {
    const [a, ar] = await Promise.all([
      api.get("/properties?archived=false"),
      api.get("/properties?archived=true"),
    ]);
    setActive(a.data); setArchived(ar.data);
  };
  useEffect(() => { load(); }, []);

  const patchForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openNew = () => {
    setForm(emptyForm());
    setWizardStep(1);
    setCreateMode(null);
    setOpen(true);
  };

  // Iter209aq — Se `?new=1` è presente (arrivo dall'onboarding pro),
  // apri automaticamente il form di creazione e pulisci la query.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") === "1") {
      openNew();
      params.delete("new");
      navigate(
        { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const submitAll = async (override, attachFiles) => {
    const form_ = override && typeof override === "object" && !override.preventDefault ? { ...form, ...override } : form;
    setCreating(true);
    try {
      // Nome visualizzato: per Privato "Via — Nome Cognome", per Azienda "Via — Ragione Sociale".
      const ownerLabel = form_.owner_type === "azienda"
        ? form_.owner_company_name
        : `${form_.owner_first_name} ${form_.owner_last_name}`.trim();
      const title = `${form_.address}${form_.city ? ", " + form_.city : ""}${ownerLabel ? " — " + ownerLabel : ""}`;
      const r = await api.post("/properties", { ...form_, title, surface_sqm: 0, rooms: 0 });
      if (attachFiles?.length) await attachAnalyzedDocs(r.data?._id || r.data?.id, attachFiles);
      toast.success("Immobile creato");
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setCreating(false);
    }
  };

  const renderCard = (p, i) => {
    const unread = perProperty[p._id] || 0;
    return (
    <Link to={`/properties/${p._id}`} key={p._id} className="fade-up" data-testid={`property-${p._id}`}>
      <div className={`kpi-card overflow-hidden relative ${p.is_archived ? "opacity-70" : ""}`}>
        {unread > 0 && (
          <span
            className="absolute top-2 right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow-lg shadow-red-500/40 ring-2 ring-white z-10"
            data-testid={`property-badge-${p._id}`}
            title={`${unread} notifiche non lette per questo immobile`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        <div className="hidden md:flex h-24 items-center justify-center rounded-lg mb-3" data-testid={`property-cover-${p._id}`} style={{ background: "linear-gradient(135deg, #ede9fe 0%, #e9d5ff 100%)" }}>
          <Buildings size={48} weight="duotone" color="#7c3aed" />
        </div>
        {p.is_archived && <div className="label-eyebrow text-amber-700">Archiviato</div>}
        <h3 className="font-display text-lg font-semibold mt-1 leading-tight pr-7 md:pr-0">{ownerDisplayName(p)}</h3>
        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
          <MapPin size={14} /> {p.address}, {p.city}
        </div>
        {p.owner_phone && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
            <User size={12} /> {p.owner_phone}
          </div>
        )}
        <PropertyDeadlineAlert property={p} />
      </div>
    </Link>
    );
  };

  return (
    <div>
      <DemoBanner />
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-eyebrow">Archivio</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
            Immobili
            <HelpTip text="Registra immobili con indirizzo e proprietario. Puoi archiviare gli immobili non più in gestione mantenendone lo storico." />
          </h1>
          <p className="text-muted-foreground mt-2">Gestisci la documentazione digitale di ogni immobile</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { if (v) openNew(); else setOpen(false); }}>
          <DialogTrigger asChild>
            <Button className="ht-gradient border-0" data-testid="new-property-btn" onClick={openNew}>
              <Plus size={16} className="mr-2" weight="bold" />Nuovo immobile
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {createMode === "manual" ? `Nuovo Immobile — Fase ${wizardStep} di 2` : createMode === "ai" ? "Nuovo Immobile — Creazione con AI" : "Nuovo Immobile"}
              </DialogTitle>
            </DialogHeader>
            {createMode === null && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2" data-testid="new-prop-mode-choice">
                <button type="button" onClick={() => setCreateMode("manual")}
                  className="p-4 rounded-lg border-2 text-left hover:border-purple-500 hover:bg-purple-50 transition-colors"
                  data-testid="new-prop-mode-manual">
                  <div className="font-semibold">📝 Creazione manuale</div>
                  <div className="text-xs text-muted-foreground mt-1">Compila tu i dati dell'immobile e del proprietario</div>
                </button>
                <button type="button" onClick={() => setCreateMode("ai")}
                  className="p-4 rounded-lg border-2 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                  data-testid="new-prop-mode-ai">
                  <div className="font-semibold">🤖 Creazione con AI</div>
                  <div className="text-xs text-muted-foreground mt-1">Carica i documenti, l'AI compila i dati per te</div>
                </button>
              </div>
            )}
            {createMode === "ai" && (
              <AiPropertyCreate submitting={creating} onCancel={() => setCreateMode(null)} onConfirm={submitAll} />
            )}
            {createMode === "manual" && <NewPropertyForm
              value={form}
              onChange={patchForm}
              onSubmit={submitAll}
              submitting={creating}
              submitLabel="Crea immobile"
              testidPrefix="new-prop"
              showNotes={true}
              wizard={true}
              clientPicker={true}
              onStepChange={setWizardStep}
            />}
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Cerca per via, città, proprietario, ragione sociale o P.IVA…"
          className="w-full max-w-xl h-11 border rounded-lg px-4 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          data-testid="properties-search" />
      </div>

      <Tabs defaultValue="active">
        <div className="mb-4">
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active-props">Attivi ({active.length})</TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived-props">Archiviati ({archived.length})</TabsTrigger>
          </TabsList>
        </div>
        {(() => {
          const q = search.trim().toLowerCase();
          // Ricerca anche per ragione sociale e P.IVA (spazi e prefisso "IT" ignorati).
          const compact = (v) => String(v || "").toLowerCase().replace(/[\s.\-]/g, "").replace(/^it(?=\d)/, "");
          const qCompact = compact(q);
          const filter = (list) => !q ? list : list.filter(p => {
            const hay = [p.address, p.city, p.owner_first_name, p.owner_last_name, p.owner_company_name, p.owner_vat_number].filter(Boolean).join(" ").toLowerCase();
            if (hay.includes(q)) return true;
            const vat = compact(p.owner_vat_number);
            return !!vat && qCompact.length >= 3 && vat.includes(qCompact);
          });
          // Iter209dzy — Ordinamento Attivi a 3 gruppi:
          //   0) almeno una scadenza già passata (prima di oggi),
          //   1) almeno una scadenza entro i prossimi 15 giorni,
          //   2) tutti gli altri nell'ordine attuale (creazione decrescente).
          // Nei gruppi 0/1 vince la scadenza più vecchia/vicina.
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const WIN = 15 * 24 * 60 * 60 * 1000;
          const parseIso = (s) => {
            const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || "");
            return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
          };
          const deadlineRank = (p) => {
            const list = Array.isArray(p.scadenze) ? p.scadenze : [];
            let overdue = null;
            let upcoming = null;
            for (const s of list) {
              const d = parseIso(s.data_scadenza);
              if (!d) continue;
              const diff = d.getTime() - today.getTime();
              if (diff < 0) {
                if (overdue === null || diff < overdue) overdue = diff;
              } else if (diff <= WIN && (upcoming === null || diff < upcoming)) {
                upcoming = diff;
              }
            }
            if (overdue !== null) return { group: 0, key: overdue };
            if (upcoming !== null) return { group: 1, key: upcoming };
            return { group: 2, key: 0 };
          };
          const sortByDeadline = (list) => {
            const arr = list.map((p, idx) => ({ p, idx, ...deadlineRank(p) }));
            arr.sort((a, b) => (a.group - b.group) || (a.key - b.key) || (a.idx - b.idx));
            return arr.map((x) => x.p);
          };
          const fa = sortByDeadline(filter(active));
          const far = filter(archived);
          return (
            <>
              <TabsContent value="active">
                {fa.length === 0 ? (
                  <div className="kpi-card text-center py-16">
                    <Buildings size={48} className="mx-auto mb-4 text-muted-foreground" weight="duotone" />
                    <p className="text-muted-foreground">{q ? "Nessun risultato per la ricerca." : "Nessun immobile attivo. Creane uno per iniziare."}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid-active">
                    {fa.map((p, i) => renderCard(p, i))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="archived">
                {far.length === 0 ? (
                  <div className="kpi-card text-center py-16 text-muted-foreground text-sm">{q ? "Nessun risultato" : "Nessun immobile archiviato."}</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid-archived">
                    {far.map((p, i) => renderCard(p, i))}
                  </div>
                )}
              </TabsContent>
            </>
          );
        })()}
      </Tabs>
    </div>
  );
}
